// The rpg layer in the sim (phase D1, gates D1.1 and D1.2): progression and the economy as one kernel module on a scene
// with the joined fight (src/enemies/fight.ts) and, where there are things, the things module (src/things/: gathering,
// breakables, pickups and the interaction chooser).
//
// - Progression (@rpg/abilities): defeats by the hero grant experience by enemy kind (the `enemy-rewards` table);
//   levels raise the stats (the level effect's stacks add the growth), each level gives a point for the weapon's tree;
//   a bought node adds modifiers, grants a tag or swaps a move (the fight's `moveFor` asks here). `progress:xp` and
//   `progress:level` go out on the bus (the HUD's and audio's level-up moment).
// - Items and economy (@rpg/items): a tabbed inventory with stack caps (the things put gathered yields and pickups into
//   it, `item:gained`), the weapon and two charm slots (charms as modifiers; the weapon slot is the fight's weapon and a
//   quick switch moves the slot), gold, fixed drops from the enemies' tables (scattered as pickups by the things),
//   a forge's upgrades and a cooking pot's dishes, buying and selling at the merchant and the smith (@rpg-unit/shop:
//   selling below the buy price, the town's standing discount through the conditions; its kernel module joins this
//   one's), using a consumable (its effects on the hero).
// - Collected glyphs (key items) lengthen the scarf (the view reads `scarfLinks`) and the glide (the hero's controller
//   input scales, `heroScale`).
//
// One sim step: `rpg:sync` (order −12, after the things' interaction, before the fight's act) adopts a weapon quick
// switch into the slot or wields the slot's weapon, and makes the hero's ability state carry exactly the progress's
// and the equipment's effects (`AbilitySet.sync`, so a re-created fighter gets them back); a level-up heals once the
// hero's fighter is there. Defeats are heard on `combat:defeated`. The screens and the handle act at once on the state
// (as the sky's actions do: menus are not on the input tape) and sync the hero then. State: one kernel component
// (`rpg/State`), saved and hashed.
import type { GameModule, World, EventBus } from "@rpg/kernel";
import type { RngStreams } from "@rpg/runtime";
import type { ControllerInput, Interactable } from "@rpg/controller";
import { createProgression, type AbilitySet, type AbilityState, type BuyRefusal, type Progression, type ProgressState } from "@rpg/abilities";
import {
  SHOP_FAULTS,
  createCrafting,
  createEquipment,
  createInventory,
  emptyInventory,
  rollDrops,
  useItem,
  type CraftRefusal,
  type Crafted,
  type Crafting,
  type EquipRefusal,
  type Equipment,
  type EquipmentState,
  type Inventory,
  type InventoryState,
  type ShopState,
  type Shops,
  type Trade,
  type TradeRefusal,
  type UseRefusal,
} from "@rpg/items";
import { createShop, type ShopFeature } from "@rpg-unit/shop";
import type { GameEvents } from "../core";
import { HERO_ID, type Fight } from "../enemies/fight";
import type { OtherInteractables, Things } from "../things/module";
import type { Conditions } from "@rpg/story";
import { RPG_FAULTS, type RpgContent } from "./content";
import { isFresh } from "../save/fresh";

export const RPG_C = "rpg/State";

/** The rpg layer's whole state (a kernel component). */
export interface RpgState {
  progress: ProgressState;
  bag: InventoryState;
  gear: EquipmentState;
  wallet: { balance: number };
  shops: ShopState;
  /** The combat weapon last wielded or seen on the hero (a quick switch shows as a change). */
  wielding: string | null;
  /** A level-up's heal waiting for the hero's fighter. */
  heal: boolean;
  steps: number;
}

/** One thing that happened (for the handle and the specs; capped, not saved). */
export type RpgEvent = { step: number } & (
  | { type: "xp"; amount: number; source: string }
  | { type: "level"; level: number }
  | { type: "gained"; item: string; count: number; source: string }
  | { type: "spent"; item: string; count: number; sink: string }
  | { type: "gold"; amount: number; why: string }
  | { type: "learned"; tree: string; node: string }
  | { type: "equipped"; slot: string; item: string | null }
  | { type: "used"; item: string }
  | { type: "crafted"; recipe: string }
);

/** A screen a station or an NPC opens (the UI opens it outside the step). */
export interface RpgOpen {
  kind: "shop" | "forge" | "pot";
  /** The shop's id, or the station's. */
  id: string;
}

/** What the scene gives the rpg layer. */
export interface RpgHost {
  content: RpgContent;
  rng: RngStreams;
  fight: () => Fight;
  /** The scene's things (pickups, the chooser), or null where there are none. */
  things: () => Things | null;
  conditions: Conditions;
  /** The ground's height at (x, z) (the stations' and NPCs' use points). */
  groundAt(x: number, z: number): number;
  /** The stations and NPC stand-ins it registers with the chooser (default the content's market rows). */
  places?: readonly RpgContent["market"][number][];
}

/** The rpg layer, for the scene, its view, the screens and the handle. */
export interface Rpg {
  modules: GameModule<GameEvents>[];
  build(world: World): void;
  hashed: readonly string[];
  content: RpgContent;
  state(): RpgState;
  progression: Progression;
  inventory: Inventory;
  equipment: Equipment;
  crafting: Crafting;
  shops: Shops;
  /** The shop feature (@rpg-unit/shop): trades, discounts, the shopkeepers. */
  shop: ShopFeature;
  conditions: Conditions;
  /** The fight's ability set (the hero's stats live there). */
  abilities(): AbilitySet;
  /** The hero's ability state, or null before the fight places it. */
  heroStats(): AbilityState | null;
  /** The HUD's source (seam 5): level, experience toward the next and what it takes, points, total. */
  progress(): { level: number; xp: number; next: number; points: number; total: number };
  /** The equipped weapon item, or null. */
  weapon(): string | null;
  glyphs(): number;
  /** Extra scarf links the glyphs give (0 to the card's grow). */
  scarfLinks(): number;
  /** The hero controller's per-step scales: the slow status's move scale, the glyphs' glide. */
  heroScale(): NonNullable<ControllerInput["scale"]>;
  /** The discount a shop gives now (the town's standing through the conditions). */
  discount(shop: string): number;
  /** The fight's move swap for the hero (a skill tree's upgrade). */
  moveFor(entity: number, move: string): string;
  /** The combat weapons the hero owns (the fight's quick switch cycles these). */
  ownedWeapons(): string[];
  // ── actions: applied at once (screens, the handle, a test), then the hero synced ──
  grantXp(amount: number, source: string): number[];
  addItem(item: string, count: number, source: string): number;
  addGold(amount: number, why: string): void;
  /** Take items, or gold (`gold`), for a game action (a quest's hand-in); false, nothing taken, when there aren't
   *  enough. */
  take(item: string, count: number, sink: string): boolean;
  buy(shop: string, item: string, count: number): Trade | { refused: TradeRefusal };
  sell(shop: string, item: string, count: number): Trade | { refused: TradeRefusal };
  craft(recipe: string, station?: string): Crafted | { refused: CraftRefusal };
  equip(slot: string, item: string): EquipRefusal | null;
  unequip(slot: string): void;
  use(item: string): UseRefusal | null;
  learn(tree: string, node: string): BuyRefusal | null;
  /** Apply the progress and equipment to the hero now (the step does it each step). */
  syncHero(): void;
  /** The stations and NPCs as interactables for the things' chooser. */
  others: OtherInteractables;
  /** The stations and NPC stand-ins this scene has (the market's rows, or the valley's stations from the layout). */
  places: readonly RpgContent["market"][number][];
  /** Ask for an NPC's shop screen (story's dialogue: a "trade" choice; taken by the UI after the step like a
   *  station's). False when the NPC hosts no shop. */
  openShop(npc: string): boolean;
  /** The screen a station or NPC asked for this step, taken once (the UI layer polls it after the step). */
  takeOpen(): RpgOpen | null;
  events: RpgEvent[];
}

/** Build the rpg layer for a scene. */
export function createRpg(host: RpgHost): Rpg {
  const c = host.content;
  const random = host.rng.stream("rpg");
  const progression = createProgression(c.progression, c.attributes);
  const inventory = createInventory(c.items);
  const equipment = createEquipment(c.items, inventory, c.slots);
  const crafting = createCrafting(c.items, inventory, c.recipes);
  // the shops (@rpg-unit/shop): trades on this layer's bag, wallet and sold counts; each trade logged and sent out
  const shop = createShop({
    items: c.items,
    inventory,
    shops: c.shops,
    conditions: host.conditions,
    trader: () => {
      const s = state();
      return { inv: s.bag, wallet: s.wallet, state: s.shops };
    },
    reserved: (item) => reserved(item),
    beforeTrade: () => void (SHOP_FAULTS.sellAtBuyPrice = RPG_FAULTS.sellAtBuyPrice),
    onTrade: (kind, r) => {
      if (kind === "buy") {
        log({ step: step(), type: "gold", amount: -r.total, why: `buy:${r.shop}` });
        gained(r.item, r.count, "buy");
      } else {
        log({ step: step(), type: "gold", amount: r.total, why: `sell:${r.shop}` });
        spent(r.item, r.count, "sell");
      }
    },
  });
  const shops = shop.shops;
  const places = host.places ?? c.market;
  let world: World | null = null;
  let bus: EventBus<GameEvents> | null = null;
  let open: RpgOpen | null = null;
  /** The state the build made (a fresh slice restores it). */
  let initial: RpgState | null = null;
  const events: RpgEvent[] = [];
  const state = (): RpgState => {
    const e = world?.query(RPG_C)[0];
    if (!world || e === undefined) throw new Error("rpg: not built yet");
    return world.getComponent<RpgState>(e, RPG_C)!;
  };
  const log = (ev: RpgEvent): void => {
    events.push(ev);
    if (events.length > 800) events.splice(0, events.length - 600);
  };
  const step = (): number => (world ? state().steps : 0);
  const heroFighter = () => {
    try {
      return host.fight().combatState().fighters[HERO_ID] ?? null;
    } catch {
      return null;
    }
  };
  const abilities = (): AbilitySet => host.fight().combat.abilities;
  const gained = (item: string, count: number, source: string): void => {
    if (count <= 0) return;
    log({ step: step(), type: "gained", item, count, source });
    bus?.emit("item:gained", { item, count, source });
  };
  const spent = (item: string, count: number, sink: string): void => {
    if (count <= 0) return;
    log({ step: step(), type: "spent", item, count, sink });
    bus?.emit("item:spent", { item, count, sink });
  };
  const glyphs = (): number => inventory.count(state().bag, "glyph");
  const counted = (): number => Math.min(glyphs(), c.glyph.maxGlyphs);
  // a discount whose facts no source answers (a scene with no standing) never holds
  const discount = (id: string): number => shop.discount(id);
  /** Open an NPC's shop (the screen is taken after the step); false when they keep none. */
  const openShopOf = (npc: string): boolean => {
    const id = shop.open(npc, HERO_ID);
    shop.take();
    if (id) open = { kind: "shop", id };
    return id !== null;
  };
  const reserved = (item: string): number => equipment.equipped(state().gear, item);

  const syncHero = (): void => {
    if (!world) return;
    const s = state();
    const f = heroFighter();
    if (!f) return;
    const fight = host.fight();
    // the weapon: a quick switch (the fight's) moves the slot; else the slot's weapon is wielded (between moves)
    if (f.weapon !== s.wielding) {
      const item = f.weapon ? c.itemOfWeapon.get(f.weapon) : undefined;
      if (item && equipment.canEquip(s.gear, s.bag, "weapon", item) === null) s.gear.slots.weapon = item;
      s.wielding = f.weapon;
    }
    const want = c.weaponOf.get(s.gear.slots.weapon ?? "") ?? null;
    if (want !== f.weapon && !f.move && fight.combat.setWeapon(HERO_ID, want)) s.wielding = want;
    const set = abilities();
    progression.sync(set, f.stats, s.progress);
    equipment.sync(set, f.stats, s.gear);
    if (s.heal) {
      set.setBase(f.stats, "hp", set.value(f.stats, "hpMax"));
      s.heal = false;
    }
  };

  const grantXp = (amount: number, source: string): number[] => {
    const s = state();
    const g = progression.grant(s.progress, amount);
    if (g.added > 0) {
      log({ step: step(), type: "xp", amount: g.added, source });
      bus?.emit("progress:xp", { amount: g.added, source });
    }
    for (const level of g.levels) {
      log({ step: step(), type: "level", level });
      bus?.emit("progress:level", { level });
    }
    if (g.levels.length) {
      if (c.healOnLevel) s.heal = true;
      // the new level's stats at once (a defeat's hit-stop may hold the next step)
      syncHero();
    }
    return g.levels;
  };
  const addItem = (item: string, count: number, source: string): number => {
    const left = inventory.add(state().bag, item, count);
    gained(item, count - left, source);
    return left;
  };
  const addGold = (amount: number, why: string): void => {
    state().wallet.balance += amount;
    log({ step: step(), type: "gold", amount, why });
  };

  // the stations and NPC stand-ins in the chooser (talk above gathering, decision 0044's priorities)
  const entries = (): Interactable[] =>
    places.map((m) => ({ id: `rpg:${m.id}`, verb: m.verb.id, pos: { x: m.pos[0]!, y: host.groundAt(m.pos[0]!, m.pos[1]!), z: m.pos[1]! }, radius: m.reach, priority: m.kind === "npc" ? 4 : 2 }));
  const placeOf = (id: string) => places.find((p) => `rpg:${p.id}` === id);
  const others: OtherInteractables = {
    register(i) {
      for (const e of entries()) i.register(e);
    },
    // usable unless a gather holds the hero
    sync(i, busy) {
      for (const m of places) i.set(`rpg:${m.id}`, { enabled: !busy });
    },
    owns: (id) => placeOf(id) !== undefined,
    at(id) {
      const m = placeOf(id)!;
      return [m.pos[0]!, host.groundAt(m.pos[0]!, m.pos[1]!), m.pos[1]!];
    },
    fire(id) {
      const m = placeOf(id);
      if (!m) return;
      if (m.kind === "station") open = { kind: m.station === "forge" ? "forge" : "pot", id: m.id };
      else if (m.npc) openShopOf(m.npc);
    },
  };

  const rpg: Rpg = {
    modules: [],
    build(w) {
      world = w;
      const tabs = Object.fromEntries(c.tabs.map((t) => [t.id, t.stacks]));
      const s: RpgState = {
        progress: progression.init(),
        bag: emptyInventory(0, tabs),
        gear: equipment.init(),
        wallet: { balance: c.kit.gold },
        shops: { sold: {} },
        wielding: null,
        heal: false,
        steps: 0,
      };
      for (const k of c.kit.items) inventory.add(s.bag, k.item, k.count);
      for (const [slot, item] of Object.entries(c.kit.equip)) equipment.equip(s.gear, s.bag, slot, item);
      w.addComponent<RpgState>(w.createEntity(), RPG_C, s);
      initial = structuredClone(s);
    },
    hashed: [RPG_C],
    content: c,
    state,
    progression,
    inventory,
    equipment,
    crafting,
    shops,
    shop,
    conditions: host.conditions,
    abilities,
    heroStats: () => heroFighter()?.stats ?? null,
    progress() {
      const p = state().progress;
      return { level: p.level, xp: p.xp, next: progression.xpToNext(p.level), points: p.points, total: p.total };
    },
    weapon: () => state().gear.slots.weapon ?? null,
    glyphs,
    scarfLinks: () => counted() * c.glyph.links,
    heroScale() {
      const st = heroFighter()?.stats;
      const set = st ? abilities() : null;
      const move = st && set && set.attributes.has("moveScale") ? set.value(st, "moveScale") : 1;
      const n = counted();
      return { move, glideSink: c.glyph.sink ** n, glideSpeed: c.glyph.speed ** n };
    },
    discount,
    moveFor(entity, move) {
      if (entity !== HERO_ID || !world) return move;
      return progression.swaps(state().progress).get(move) ?? move;
    },
    ownedWeapons() {
      if (!world) return [...c.weaponOf.values()];
      const s = state();
      return [...c.weaponOf.entries()].filter(([item]) => inventory.count(s.bag, item) > 0).map(([, w]) => w);
    },
    grantXp,
    addItem,
    addGold,
    take(item, count, sink) {
      const s = state();
      if (item === "gold") {
        if (s.wallet.balance < count) return false;
        addGold(-count, sink);
        return true;
      }
      if (!inventory.take(s.bag, item, count)) return false;
      spent(item, count, sink);
      return true;
    },
    buy: (id, item, count) => shop.buy(id, item, count),
    sell: (id, item, count) => shop.sell(id, item, count),
    craft(recipe, station) {
      const s = state();
      const r = crafting.craft(recipe, { inv: s.bag, wallet: s.wallet, levels: s.gear.levels, ...(station ? { station } : {}) });
      if ("refused" in r) return r;
      for (const i of r.spent) spent(i.item, i.count, "craft");
      if (r.cost) log({ step: step(), type: "gold", amount: -r.cost, why: `craft:${recipe}` });
      for (const m of r.made) gained(m.item, m.count, "craft");
      log({ step: step(), type: "crafted", recipe });
      syncHero();
      return r;
    },
    equip(slot, item) {
      const s = state();
      const why = equipment.equip(s.gear, s.bag, slot, item);
      if (why) return why;
      log({ step: step(), type: "equipped", slot, item });
      syncHero();
      return null;
    },
    unequip(slot) {
      equipment.unequip(state().gear, slot);
      log({ step: step(), type: "equipped", slot, item: null });
      syncHero();
    },
    use(item) {
      const s = state();
      const st = heroFighter()?.stats;
      if (!st) return "refused";
      const why = useItem({ items: c.items, inventory, inv: s.bag, set: abilities(), stats: st, item, source: HERO_ID });
      if (why) return why;
      spent(item, 1, "use");
      log({ step: step(), type: "used", item });
      return null;
    },
    learn(tree, node) {
      const why = progression.buy(state().progress, tree, node);
      if (why) return why;
      log({ step: step(), type: "learned", tree, node });
      syncHero();
      return null;
    },
    syncHero,
    others,
    places,
    openShop: (npc) => openShopOf(npc),
    takeOpen() {
      // a station's, an NPC's (through openShop), or a shop an interact:request of kind "shop" opened
      const asked = shop.take();
      const o = open ?? (asked ? { kind: "shop" as const, id: asked } : null);
      open = null;
      return o;
    },
    events,
  };

  const module: GameModule<GameEvents> = {
    id: "rpg",
    version: "0.1.0",
    // checked declarations (decision 0077): what it emits
    provides: { events: ["progress:xp", "progress:level", "item:gained", "item:spent"] },
    setup(ctx) {
      bus = ctx.events;
      // experience, gold and the fixed drop for a defeat by the hero (by enemy kind; a burn's tick counts as its source's)
      ctx.events.on("combat:defeated", (p) => {
        if (!world || p.by !== HERO_ID || p.entity === HERO_ID) return;
        const e = host.fight().state().enemies.find((x) => x.id === p.entity);
        if (!e) return;
        const r = c.rewards.get(e.kind);
        if (!r) return;
        grantXp(r.xp, e.kind);
        if (r.gold) addGold(r.gold, `defeat:${e.kind}`);
        const table = c.drops.get(r.drops);
        if (!table) return;
        const got = rollDrops(table, () => random.next());
        const th = host.things();
        if (th) th.drop(got, [e.pos[0], e.pos[1], e.pos[2]]);
        else for (const g of got) addItem(g.item, g.count, "drop");
      });
      ctx.registerSystem(
        () => {
          const s = state();
          s.steps++;
          syncHero();
        },
        { name: "rpg:sync", order: -12 },
      );
      ctx.registerSaveSlice({
        serialize: () => structuredClone(state()),
        deserialize: (data) => {
          // a save from before the rpg layer joined the valley: a new game's state (the v6 migration's marker)
          const d = isFresh(data) ? structuredClone(initial!) : structuredClone(data as RpgState);
          const s = state();
          for (const k of Object.keys(s)) delete (s as unknown as Record<string, unknown>)[k];
          Object.assign(s, d);
        },
      });
      // the facts the rpg layer answers in the shared conditions (seam 2, @rpg/story's): level, xp (the total), gold,
      // item counts (`item.gold` is the purse, for the story's give and take) and the slots' items
      const bare = (k: string): boolean => k === "";
      host.conditions.register({ name: "level", get: () => (world ? state().progress.level : 1), knows: bare });
      host.conditions.register({ name: "xp", get: () => (world ? state().progress.total : 0), knows: bare });
      host.conditions.register({ name: "gold", get: () => (world ? state().wallet.balance : 0), knows: bare });
      host.conditions.register({ name: "item", get: (id) => (!world ? 0 : id === "gold" ? state().wallet.balance : inventory.count(state().bag, id)), knows: (id) => id === "gold" || c.items.has(id), keys: () => c.items.list().map((i) => i.id) });
      host.conditions.register({ name: "equipped", get: (slot) => (world ? (state().gear.slots[slot] ?? null) : null), knows: (slot) => c.slots.some((s) => s.id === slot), keys: () => c.slots.map((s) => s.id) });
    },
  };
  // the shop feature's own module: an interact:request of kind "shop" opens a keeper's shop; it sends interact:done
  rpg.modules = [module, shop.module<GameEvents>()];
  return rpg;
}
